import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getBill } from '../../actions/billingActions';
import { downloadBillPDF, downloadBillImage } from '../../utils/billPdf';
import Loader from '../layouts/Loader';
import MetaData from '../layouts/MetaData';
import hospitalInfo from '../../constants/hospitalInfo';
import formatDate from '../../utils/formatDate';

export default function PublicInvoice() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const dispatch = useDispatch();
    const { bill, loading, error } = useSelector(state => state.billingState);

    useEffect(() => {
        dispatch(getBill(id, token));
    }, [dispatch, id, token]);

    const patientName = bill?.patient?.name || (typeof bill?.patient === 'string' ? bill?.patient : '') || 'Valued Patient';
    const patientId = bill?.patient?.patientId || '';
    const websiteUrl = hospitalInfo.website || 'https://greennaturopathy.com';
    const websiteDisplay = hospitalInfo.websiteDisplay || 'greennaturopathy.com';
    const reviewUrl = hospitalInfo.googleReviewUrl || 'https://g.page/r/CfNAbnnuCG74EAI/review';

    const currency = (n) =>
        `₹${Number(n || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Paid':
                return { bg: '#ecfdf5', color: '#065f46', border: '#a7f3d0', label: 'Paid' };
            case 'Partially Paid':
                return { bg: '#fffbeb', color: '#92400e', border: '#fde68a', label: 'Partially Paid' };
            case 'Cancelled':
                return { bg: '#fef2f2', color: '#991b1b', border: '#fecaca', label: 'Cancelled' };
            default:
                return { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1', label: status || 'Pending' };
        }
    };

    const statusBadge = getStatusBadge(bill?.status);

    return (
        <>
            <MetaData title={`Invoice ${bill?.invoiceNumber || ''}`} />
            <div className="container py-4 my-3 text-center" style={{ maxWidth: '640px' }}>
                {loading ? (
                    <div className="py-5">
                        <Loader />
                    </div>
                ) : error ? (
                    <div className="card p-5 shadow-sm mx-auto border-0" style={{ borderRadius: '20px', background: '#fff' }}>
                        <i className="fa fa-shield-alt text-danger mb-3" style={{ fontSize: '3.5rem' }}></i>
                        <h4 className="text-danger fw-bold mb-2">Access Denied</h4>
                        <p className="text-muted mb-0">{error}</p>
                        <p className="text-secondary small mt-3">
                            Please open the official link sent directly to your phone via WhatsApp or SMS.
                        </p>
                        <div className="mt-4 pt-3 border-top">
                            <a
                                href={websiteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-success btn-sm px-3 rounded-pill"
                            >
                                <i className="fa fa-globe me-1"></i> Visit {websiteDisplay}
                            </a>
                        </div>
                    </div>
                ) : (
                    <div className="card shadow-sm border-0 mx-auto overflow-hidden text-start" style={{ borderRadius: '20px', background: '#fff' }}>
                        {/* Header Banner with Brand */}
                        <div className="p-4 text-white text-center" style={{ background: 'linear-gradient(135deg, #1b4d3e 0%, #2e7d32 100%)' }}>
                            <img
                                src={hospitalInfo.logoUrl || '/images/logo-light.jpg'}
                                alt="Green Naturopathy"
                                className="img-fluid rounded-3 mb-2 bg-white p-1 shadow-sm"
                                style={{ maxHeight: '55px', objectFit: 'contain' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <h3 className="fw-bold mb-1 tracking-wide" style={{ letterSpacing: '0.5px' }}>
                                {hospitalInfo.centreName || 'GREEN NATUROPATHY'}
                            </h3>
                            <p className="small mb-0 text-white-50">
                                {hospitalInfo.tagline || 'Pain Clinic & Holistic Healthcare'}
                            </p>
                        </div>

                        <div className="card-body p-4">
                            {/* Patient Greeting & Name */}
                            <div className="text-center mb-4">
                                <span className="badge bg-success bg-opacity-10 text-success px-3 py-2 rounded-pill fw-semibold mb-2" style={{ fontSize: '0.82rem' }}>
                                    <i className="fa fa-check-circle me-1"></i> Official Verified Invoice
                                </span>
                                <h4 className="fw-bold text-dark mb-1">
                                    Hello, <span style={{ color: '#2e7d32' }}>{patientName}</span>!
                                </h4>
                                <p className="text-muted small mb-0">
                                    Your official treatment invoice is ready for download.
                                </p>
                            </div>

                            {/* Invoice Summary Card */}
                            <div className="p-3 rounded-3 mb-4" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                <div className="row g-2 align-items-center mb-3">
                                    <div className="col-6">
                                        <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: '0.75rem' }}>Invoice Number</div>
                                        <div className="font-monospace fw-bold text-dark">{bill?.invoiceNumber}</div>
                                    </div>
                                    <div className="col-6 text-end">
                                        <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: '0.75rem' }}>Invoice Date</div>
                                        <div className="fw-semibold text-dark">{formatDate(bill?.createdAt || bill?.date) || '—'}</div>
                                    </div>
                                </div>

                                <div className="row g-2 align-items-center pt-2 border-top">
                                    <div className="col-6">
                                        <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: '0.75rem' }}>Patient Name</div>
                                        <div className="fw-bold text-dark">
                                            {patientName}
                                            {patientId && <span className="badge bg-light text-muted border ms-2 small">ID: {patientId}</span>}
                                        </div>
                                    </div>
                                    <div className="col-6 text-end">
                                        <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: '0.75rem' }}>Status</div>
                                        <span
                                            style={{
                                                background: statusBadge.bg,
                                                color: statusBadge.color,
                                                border: `1px solid ${statusBadge.border}`,
                                                padding: '0.2rem 0.65rem',
                                                borderRadius: '6px',
                                                fontSize: '0.78rem',
                                                fontWeight: 600,
                                                display: 'inline-block'
                                            }}
                                        >
                                            {statusBadge.label}
                                        </span>
                                    </div>
                                </div>

                                <div className="row g-2 align-items-center pt-2 mt-2 border-top">
                                    <div className="col-6">
                                        <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: '0.75rem' }}>Total Amount</div>
                                        <div className="fs-5 fw-bold" style={{ color: '#1b4d3e' }}>
                                            {currency(bill?.totalAmount)}
                                        </div>
                                    </div>
                                    <div className="col-6 text-end">
                                        {Number(bill?.balanceDue || 0) > 0 ? (
                                            <>
                                                <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: '0.75rem' }}>Balance Due</div>
                                                <div className="fw-bold text-danger">{currency(bill?.balanceDue)}</div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: '0.75rem' }}>Amount Paid</div>
                                                <div className="fw-bold text-success">{currency(bill?.amountPaid || bill?.totalAmount)}</div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Download Action Buttons */}
                            <div className="d-grid gap-2 mb-4">
                                <button
                                    className="btn btn-lg text-white shadow-sm d-flex align-items-center justify-content-center"
                                    style={{ background: '#2e7d32', borderColor: '#2e7d32', fontWeight: 600, borderRadius: '12px' }}
                                    onClick={() => downloadBillPDF(bill, bill?.patient, 'digital')}
                                    disabled={!bill || !bill.patient}
                                >
                                    <i className="fa fa-file-pdf me-2 fs-5"></i> Download Digital PDF
                                </button>
                                <button
                                    className="btn btn-lg btn-outline-secondary shadow-sm d-flex align-items-center justify-content-center"
                                    style={{ fontWeight: 600, borderRadius: '12px' }}
                                    onClick={() => downloadBillImage(bill, bill?.patient, 'digital')}
                                    disabled={!bill || !bill.patient}
                                >
                                    <i className="fa fa-image me-2 fs-5"></i> Download Invoice Image
                                </button>
                            </div>

                            {/* Google Review Card */}
                            <div className="p-3 rounded-3 text-center mb-3" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
                                <div className="d-flex justify-content-center align-items-center gap-1 mb-1" style={{ color: '#f59e0b', fontSize: '1.25rem' }}>
                                    <i className="fa fa-star"></i>
                                    <i className="fa fa-star"></i>
                                    <i className="fa fa-star"></i>
                                    <i className="fa fa-star"></i>
                                    <i className="fa fa-star"></i>
                                </div>
                                <div className="fw-bold text-dark mb-1" style={{ fontSize: '0.98rem' }}>
                                    How was your experience with Green Naturopathy?
                                </div>
                                <p className="text-muted small mb-3">
                                    Your feedback helps us heal more lives naturally. Please take 30 seconds to rate us and share your review on Google!
                                </p>
                                <a
                                    href={reviewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm text-white fw-bold px-4 py-2 rounded-pill shadow-sm text-decoration-none d-inline-flex align-items-center"
                                    style={{ background: '#ea4335', borderColor: '#ea4335' }}
                                >
                                    <i className="fab fa-google me-2"></i>
                                    Rate Us &amp; Write a Google Review
                                    <i className="fa fa-external-link-alt ms-2 small"></i>
                                </a>
                            </div>

                            {/* Official Website Banner */}
                            <div className="p-3 rounded-3 text-center mb-3" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                                <div className="small fw-bold text-success mb-1">
                                    <i className="fa fa-globe me-1"></i> Green Naturopathy Official Website
                                </div>
                                <p className="text-muted small mb-2">
                                    Learn more about our natural treatments, dietary therapies & holistic healthcare services:
                                </p>
                                <a
                                    href={websiteUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm text-white fw-bold px-4 py-2 rounded-pill shadow-sm text-decoration-none d-inline-flex align-items-center"
                                    style={{ background: '#2e7d32', borderColor: '#2e7d32' }}
                                >
                                    <i className="fa fa-external-link-alt me-2 small"></i>
                                    Visit {websiteDisplay}
                                </a>
                            </div>

                            {/* Contact & Footer Info */}
                            <div className="text-center pt-2 text-muted small border-top">
                                <div className="fw-semibold text-dark mb-1">{hospitalInfo.centreName || 'Green Naturopathy'}</div>
                                <div>{hospitalInfo.addressLine1}, {hospitalInfo.addressLine2}</div>
                                <div className="mt-1">
                                    <span>Phone: <a href={`tel:+91${hospitalInfo.phone || '9345316319'}`} className="text-decoration-none text-success fw-semibold">+91 {hospitalInfo.phone || '9345316319'}</a></span>
                                    <span className="mx-2">&bull;</span>
                                    <span>Email: <a href={`mailto:${hospitalInfo.email || 'greennaturopathycbe@gmail.com'}`} className="text-decoration-none text-muted">{hospitalInfo.email || 'greennaturopathycbe@gmail.com'}</a></span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
